/*
 * The console home's "Do next" list: a ranked list of what to work on to grow
 * earnings, built from data the backend already serves.
 *
 * Pure functions only (no React, no react-router: CRA's Jest cannot load
 * react-router-dom v7), so the ranking is unit-tested in doNext.test.js.
 *
 * Every item is
 *   { id, kind, tier, score, domainId, site, title, why, valueUsd,
 *     link: { to | href, label }, links: [...], followUp?, action? }
 * and rankItems() orders them by tier, then within a tier:
 *   0  follow-ups overdue           most overdue first
 *   1  follow-ups due today         + unanswered reader feedback and contact messages
 *   2  data chores                  stale or failing sources, a missing Amazon report
 *   3  earnings work                search opportunities, thin pages and traffic
 *                                   drops, by $/mo at stake
 *   4  follow-ups due this week     soonest first
 */
import { normalizeHost, mstDayWindow, sumOverDays } from '../domainMetrics';
import { daysBetween, todayIso } from '../timeline/timeline';
import { thinFix } from '../seo/seoThin';
import { slugOf } from '../rankings/rankings';

export const TIERS = { overdue: 0, due: 1, chore: 2, value: 3, upcoming: 4 };

export const KIND_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'follow-up', label: 'Follow-ups' },
  { key: 'search', label: 'Search' },
  { key: 'thin', label: 'Thin pages' },
  { key: 'drop', label: 'Traffic drops' },
  { key: 'inbox', label: 'Messages' },
  { key: 'chore', label: 'Data chores' },
];

/* A site with almost no pages and no AdSense earnings is a parked or
   infrastructure-only domain (a holding page, a redirect, a company site):
   nothing on it to grow, so its search and thin-page items are hidden unless
   the viewer asks for them. Follow-ups always show: someone wrote them. */
export const PARKED_MAX_PAGES = 2;
export const isParked = (domain, earnings30 = 0) =>
  !!domain && (Number(domain.page_count) || 0) <= PARKED_MAX_PAGES && !(earnings30 > 0.005);

export const siteName = (domain, fallbackId = '') =>
  (domain && (domain.hostname || domain.name)) || String(fallbackId).replace(/_/g, '.');

export const editorPath = (domainId, slug) =>
  `/domain/${encodeURIComponent(domainId)}/pages/${encodeURIComponent(slug)}/edit`;

const cleanSlug = (slug) => String(slug || '').replace(/^\/+|\/+$/g, '');

const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US');

/* "$0.84", "<$0.01", "$12" */
export const fmtUsd = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (n > 0 && n < 0.01) return '<$0.01';
  if (n >= 100) return `$${Math.round(n).toLocaleString('en-US')}`;
  return `$${n.toFixed(2)}`;
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];
const SHORT = MONTHS.map((m) => m.slice(0, 3));

const shortDate = (iso) => {
  const [, m, d] = String(iso).split('-').map(Number);
  return m && d ? `${SHORT[m - 1]} ${d}` : String(iso || '');
};

/* What one more visit is worth across the fleet (AdSense earnings per page
   view), over sites that have both: used to put a dollar figure on clicks for
   a site whose own value per visit is unknown (no AdSense yet). */
export const fleetValuePerVisit = (domains, { hits, earnings }) => {
  let e = 0;
  let h = 0;
  (domains || []).forEach((d) => {
    const dh = hits(d);
    const de = earnings(d);
    if (dh > 0 && de > 0) { e += de; h += dh; }
  });
  return h > 0 ? e / h : 0;
};

/* ───────────────────────────── follow-ups ───────────────────────────── */

export const followUpItems = (followUps, domainsById = {}, today = todayIso()) =>
  (followUps || []).map((f) => {
    const state = f.state || (f.due_date && f.due_date < today ? 'overdue'
      : f.due_date === today ? 'due' : 'upcoming');
    const days = f.due_date ? daysBetween(today, f.due_date) : null;
    const why = state === 'overdue'
      ? `Overdue by ${-days} day${days === -1 ? '' : 's'} (was due ${shortDate(f.due_date)})`
      : state === 'due' ? 'Due today'
        : f.due_date ? `Due ${shortDate(f.due_date)}${days != null ? ` (in ${days} day${days === 1 ? '' : 's'})` : ''}`
          : 'No due date';
    const page = cleanSlug(f.page);
    const domain = domainsById[f.domain_id];
    return {
      id: `fu:${f.domain_id}:${f.id}`,
      kind: 'follow-up',
      tier: TIERS[state] ?? TIERS.upcoming,
      // most overdue first; then soonest due
      score: days == null ? -9999 : -days,
      domainId: f.domain_id,
      site: f.domain_name || siteName(domain, f.domain_id),
      title: f.text,
      why: page ? `${why} · /${page}` : why,
      valueUsd: null,
      link: page
        ? { to: editorPath(f.domain_id, page), label: 'Open page' }
        : { to: `/domain/${encodeURIComponent(f.domain_id)}`, label: 'Open site' },
      links: (f.links || []).filter((l) => l && l.url).slice(0, 2)
        .map((l) => ({ href: l.url, label: l.label || 'Link' })),
      followUp: { domainId: f.domain_id, id: f.id },
      state,
    };
  });

/* ───────────────────────── search opportunities ─────────────────────── */

export const MIN_SEARCH_GAIN_CLICKS = 3;

/* One item per query sitting just below the top (backend: position 8–20 with
   30+ impressions). The upside is the backend's own estimate of the extra
   clicks a month at position 3, in dollars at the site's value per visit;
   when the site has no AdSense value per visit the fleet's is used to rank
   it, and the text says only clicks. */
export const searchItems = (opportunities, domainsById = {}, { fleetVpv = 0, hideParked = true, earningsOf = () => 0 } = {}) =>
  (opportunities || [])
    .filter((q) => (q.gain_top3_clicks || 0) >= MIN_SEARCH_GAIN_CLICKS)
    .filter((q) => {
      const d = domainsById[q.domain_id];
      return !(hideParked && d && isParked(d, earningsOf(d)));
    })
    .map((q) => {
      const domain = domainsById[q.domain_id];
      const clicks = q.gain_top3_clicks || 0;
      const ownUsd = q.gain_top3_usd || 0;
      const valueUsd = ownUsd > 0 ? ownUsd : clicks * fleetVpv;
      const pos = Number(q.position).toFixed(q.position >= 10 ? 0 : 1);
      const searches = q.est_monthly_searches ? `~${fmtInt(q.est_monthly_searches)} searches a month` : null;
      const gain = `could bring ~${fmtInt(clicks)} more clicks a month${ownUsd > 0 ? ` (~${fmtUsd(ownUsd)}/mo)` : ''}`;
      const slug = q.top_page ? slugOf(q.top_page) : '';
      const rankings = { to: `/domain/${encodeURIComponent(q.domain_id)}/rankings`, label: 'Rankings' };
      return {
        id: `sc:${q.domain_id}:${q.query}`,
        kind: 'search',
        tier: TIERS.value,
        score: valueUsd + clicks * 1e-6,
        domainId: q.domain_id,
        site: siteName(domain, q.domain_id),
        title: `Push “${q.query}” onto the top of Google`,
        why: `Ranks #${pos}${searches ? ` for ${searches}` : ''}; reaching the top 3 ${gain}.`,
        valueUsd,
        estimated: !(ownUsd > 0),
        link: slug ? { to: editorPath(q.domain_id, slug), label: 'Edit page' } : rankings,
        links: slug ? [rankings] : [],
      };
    });

/* ────────────────────────────── thin pages ──────────────────────────── */

export const MIN_THIN_VIEWS = 10;
export const THIN_PER_SITE = 3;

/* Thin pages that still get traffic: the site tells Google not to index them
   (noindex) until they are filled out, so each is earning less than it could.
   Ranked by what the page earned in the last 30 days (measured or the page
   metrics' estimate); with no earnings figure, views x the site's RPM. Only
   the top THIN_PER_SITE per site are listed; the rest roll up into one item
   linking to the site's page list filtered to thin pages. */
export const thinItems = (domainId, domain, thinResp, metricsResp, { siteRpm = 0, fleetVpv = 0 } = {}) => {
  const verdicts = (thinResp && thinResp.pages) || {};
  const bySlug = {};
  Object.entries(verdicts).forEach(([slug, v]) => { bySlug[cleanSlug(slug)] = v; });
  const rows = ((metricsResp && metricsResp.pages) || [])
    .map((p) => ({ ...p, slug: cleanSlug(p.slug), verdict: bySlug[cleanSlug(p.slug)] }))
    .filter((p) => p.verdict && p.verdict.thin && !p.verdict.exempt && (p.hits || 0) >= MIN_THIN_VIEWS)
    .map((p) => {
      const earned = p.earnings > 0 ? p.earnings
        : siteRpm > 0 ? (p.hits * siteRpm) / 1000 : p.hits * fleetVpv;
      return { ...p, earned };
    })
    .sort((a, b) => b.earned - a.earned || b.hits - a.hits);
  const site = siteName(domain, domainId);
  const items = rows.slice(0, THIN_PER_SITE).map((p) => {
    const reasons = p.verdict.reasons || [];
    const fix = thinFix(reasons[0]);
    return {
      id: `thin:${domainId}:${p.slug}`,
      kind: 'thin',
      tier: TIERS.value,
      score: p.earned + p.hits * 1e-6,
      domainId,
      site,
      title: `Fill out /${p.slug || ''} so Google can index it`,
      why: `${fmtInt(p.hits)} views and ~${fmtUsd(p.earned)} in 30 days, but hidden from Google: `
        + `${reasons.join('; ') || 'thin'}.${fix ? ` ${fix}` : ''}`,
      valueUsd: p.earned,
      estimated: !(p.earnings > 0),
      link: { to: editorPath(domainId, p.slug), label: 'Edit page' },
      links: [],
    };
  });
  const rest = rows.slice(THIN_PER_SITE);
  if (rest.length) {
    const restUsd = rest.reduce((s, p) => s + p.earned, 0);
    const restViews = rest.reduce((s, p) => s + (p.hits || 0), 0);
    items.push({
      id: `thin-rest:${domainId}`,
      kind: 'thin',
      tier: TIERS.value,
      // a roll-up is worth less attention than its best page; rank it by its average page
      score: restUsd / rest.length,
      domainId,
      site,
      title: `${fmtInt(rest.length)} more thin page${rest.length === 1 ? '' : 's'} with traffic`,
      why: `${fmtInt(restViews)} views and ~${fmtUsd(restUsd)} in 30 days across them, all hidden from Google.`,
      valueUsd: restUsd,
      estimated: true,
      link: { to: `/domain/${encodeURIComponent(domainId)}/pages?thin=1`, label: 'Thin pages' },
      links: [],
    });
  }
  return items;
};

/* Which sites to check for thin pages: fetching verdicts and page metrics is a
   couple of requests per site, so only the top earners (then by traffic). */
export const thinCandidateSites = (domains, { hits, earnings }, n = 5) =>
  (domains || [])
    .filter((d) => !isParked(d, earnings(d)) && hits(d) > 0)
    .sort((a, b) => earnings(b) - earnings(a) || hits(b) - hits(a))
    .slice(0, n);

/* ──────────────────────────── data freshness ────────────────────────── */

/* Each cached source and how often its scheduled job runs. A source is stale
   once it has missed a run by a comfortable margin. */
export const SOURCES = [
  { key: 'hits', label: 'Page views', maxAgeH: 30 },
  { key: 'earnings', label: 'AdSense earnings', maxAgeH: 30 },
  { key: 'impressions', label: 'Ad impressions', maxAgeH: 30 },
  { key: 'stats', label: 'Page stats', maxAgeH: 13 },
];

const parseMs = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/* -> { asOf: ISO of the oldest healthy source (every number is at least this
   fresh) or null, problems: [{ key, label, state: 'stale'|'failing'|'never', updatedAt, ageH }] } */
export const dataFreshness = (updatedAt = {}, failed = {}, now = new Date()) => {
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  let oldest = null;
  const problems = [];
  SOURCES.forEach((s) => {
    if (failed[s.key]) {
      problems.push({ key: s.key, label: s.label, state: 'failing', updatedAt: updatedAt[s.key] || null, ageH: null });
      return;
    }
    const ms = parseMs(updatedAt[s.key]);
    if (ms == null) {
      problems.push({ key: s.key, label: s.label, state: 'never', updatedAt: null, ageH: null });
      return;
    }
    const ageH = (nowMs - ms) / 3600000;
    if (ageH > s.maxAgeH) problems.push({ key: s.key, label: s.label, state: 'stale', updatedAt: updatedAt[s.key], ageH });
    if (oldest == null || ms < oldest) oldest = ms;
  });
  return { asOf: oldest == null ? null : new Date(oldest).toISOString(), problems };
};

const sameLocalDay = (a, b) => a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const clock = (d) => {
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h % 12 || 12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
};

/* "6:00 AM today", "11:30 PM yesterday", "Oct 30, 3:00 AM" (viewer's local time) */
export const asOfLabel = (iso, now = new Date()) => {
  const ms = parseMs(iso);
  if (ms == null) return '';
  const d = new Date(ms);
  if (sameLocalDay(d, now)) return `${clock(d)} today`;
  const y = new Date(now.getTime());
  y.setDate(y.getDate() - 1);
  if (sameLocalDay(d, y)) return `${clock(d)} yesterday`;
  return `${SHORT[d.getMonth()]} ${d.getDate()}, ${clock(d)}`;
};

const ageText = (h) => (h < 48 ? `${Math.round(h)} hours` : `${Math.round(h / 24)} days`);

export const choreItemsForSources = (problems) => (problems || []).map((p) => ({
  id: `src:${p.key}`,
  kind: 'chore',
  tier: TIERS.chore,
  score: p.state === 'failing' ? 1000 : p.state === 'never' ? 500 : p.ageH || 0,
  domainId: null,
  site: 'All sites',
  title: p.state === 'failing' ? `${p.label} could not be loaded`
    : p.state === 'never' ? `${p.label}: no refresh time on record`
      : `${p.label} are ${ageText(p.ageH)} old`,
  why: p.state === 'failing'
    ? 'The console could not read this source, so the numbers built on it are missing.'
    : p.state === 'never'
      ? 'The console could not tell when this source was last refreshed: it failed to load or has never been pulled.'
      : 'Its scheduled refresh has not run on time, so the numbers built on it are out of date. '
        + 'Refresh it by hand in Settings → Data sources, then check the job.',
  valueUsd: null,
  link: { to: '/settings#data-sources', label: 'Data sources' },
  links: [],
}));

/* ───────────────────────────── Amazon report ────────────────────────── */

const pad = (n) => String(n).padStart(2, '0');

/* The last full calendar month before `now`: { start, end, label, name } */
export const lastFullMonth = (now = new Date()) => {
  const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const m = now.getMonth() === 0 ? 12 : now.getMonth(); // 1-based previous month
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { start: `${y}-${pad(m)}-01`, end: `${y}-${pad(m)}-${pad(last)}`, name: MONTHS[m - 1], year: y };
};

/* A "Import the October Amazon report" chore when no imported Amazon period
   covers the whole of last month. Null when it is covered, or when there is no
   Amazon data to judge by (the request failed). */
export const amazonReportChore = (amazon, now = new Date()) => {
  if (!amazon || amazon.error) return null;
  const month = lastFullMonth(now);
  const covered = (amazon.periods || []).some((p) =>
    p.period_start && p.period_end && p.period_start <= month.start && p.period_end >= month.end);
  if (covered) return null;
  const sites = Object.keys(amazon.tags || {}).length;
  return {
    id: `amazon:${month.start}`,
    kind: 'chore',
    tier: TIERS.chore,
    score: 100,
    domainId: null,
    site: 'All sites',
    title: `Import the ${month.name} Amazon report`,
    why: `No Amazon Associates report covering ${month.name} ${month.year} has been imported, so Amazon earnings`
      + `${sites ? ` for the ${sites} tagged site${sites === 1 ? '' : 's'}` : ''} are missing from the totals.`,
    valueUsd: null,
    action: 'amazon-import',
    link: { href: 'https://affiliate-program.amazon.com/p/reporting/earnings', label: 'Associates reports' },
    links: [],
  };
};

/* ───────────────────────────── weekly change ────────────────────────── */

/* The daily series traffic is judged on: probably-human views when the
   container has them (metrics.trafficDaily), every view otherwise. */
export const viewsDaily = (metrics) => metrics.trafficDaily || metrics.hitsDaily;
const viewsWord = (metrics) => (metrics.trafficDaily ? 'human views' : 'views');

/* The last 28 complete MST days, split into the latest week and the three
   before it: { recent: [7 day keys], baseline: [[7], [7], [7]], all: [28] }. */
export const weekWindows = (now = new Date()) => {
  const all = mstDayWindow(28, now);
  return { all, recent: all.slice(21), baseline: [all.slice(0, 7), all.slice(7, 14), all.slice(14, 21)] };
};

const median = (xs) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/* A site's latest week against a typical week before it. "Typical" is 7 x
   the median DAY of the 21 days before (missing days count as 0), not a sum
   or a median of weekly sums: a proxy-bot crawl of a few days can straddle
   two baseline weeks (archerydistrict's late-October crawl did, and read as
   a 93% "drop" afterwards), but it only ever moves a handful of the 21 days.
   -> { recent, typical, change } where change is a fraction (-0.4 = down
   40%), null when there is no baseline. */
export const weekChange = (daily, windows) => {
  const recent = sumOverDays(daily, windows.recent);
  const days = windows.baseline.flat().map((k) => sumOverDays(daily, [k]));
  const typical = days.length ? 7 * median(days) : 0;
  return { recent, typical, change: typical > 0 ? (recent - typical) / typical : null };
};

/* Plain week over week (the latest 7 days against the 7 before), for the
   headline numbers, where "vs previous 7 days" is what a reader expects. */
export const weekOverWeek = (daily, windows) => {
  const recent = sumOverDays(daily, windows.recent);
  const prev = sumOverDays(daily, windows.baseline[2]);
  return { recent, prev, change: prev > 0 ? (recent - prev) / prev : null };
};

/* Same split, ending on an explicit 'YYYY-MM-DD' (inclusive) instead of
   yesterday: AdSense's daily report can lag a day behind page views, and a
   missing last day must not read as a 1/7 drop. Null for a bad date. */
export const weekWindowsEndingAt = (endIso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(endIso || ''));
  if (!m) return null;
  const end = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const all = [];
  for (let i = 27; i >= 0; i--) all.push(new Date(end - i * 86400000).toISOString().slice(0, 10));
  return { all, recent: all.slice(21), baseline: [all.slice(0, 7), all.slice(7, 14), all.slice(14, 21)] };
};

/* Fleet totals for the headline: page views and AdSense for the latest week
   against the week before, and RPM over the AdSense window (views and
   earnings on the same days). Parked sites count: their views are real. */
export const fleetWeek = (domains, metrics, viewWindows, earnWindows) => {
  const sum = (dailyOf, w) => (domains || []).reduce((acc, d) => {
    const r = weekOverWeek(dailyOf(d), w);
    return { recent: acc.recent + r.recent, prev: acc.prev + r.prev };
  }, { recent: 0, prev: 0 });
  const change = ({ recent, prev }) => ({ recent, prev, change: prev > 0 ? (recent - prev) / prev : null });
  // views: what traffic is judged on (human when labelled); allViews: every
  // view, which is also what RPM divides by, since AdSense pays for all of it
  const views = change(sum(viewsDaily(metrics), viewWindows));
  const allViews = metrics.trafficDaily ? change(sum(metrics.hitsDaily, viewWindows)) : views;
  if (!earnWindows || !metrics.earningsDaily) return { views, allViews, earnings: null, rpm: null };
  const earnings = change(sum(metrics.earningsDaily, earnWindows));
  const viewsOnEarnDays = sum(metrics.hitsDaily, earnWindows);
  const rpmOf = (e, v) => (v > 0 ? (e / v) * 1000 : null);
  const rpmNow = rpmOf(earnings.recent, viewsOnEarnDays.recent);
  const rpmPrev = rpmOf(earnings.prev, viewsOnEarnDays.prev);
  return {
    views,
    allViews,
    earnings,
    rpm: { recent: rpmNow, prev: rpmPrev, change: rpmNow != null && rpmPrev > 0 ? (rpmNow - rpmPrev) / rpmPrev : null },
  };
};

/* ───────────────────────────── traffic drops ────────────────────────── */

export const DROP_MIN_TYPICAL_VIEWS = 70; // about 10 a day; below that a week is noise
export const DROP_MIN_CHANGE = -0.3;

const pct = (f) => `${Math.round(Math.abs(f) * 100)}%`;

/* One item per site whose latest week of page views is 30%+ below its typical
   week. The money at stake is the lost views over a month, at the site's own
   value per visit (30-day AdSense / views), or the fleet's when it has none. */
export const dropItems = (domains, metrics, windows, { fleetVpv = 0, hideParked = true } = {}) =>
  (domains || [])
    .filter((d) => !(hideParked && isParked(d, metrics.earnings(d))))
    .map((d) => ({ d, w: weekChange(viewsDaily(metrics)(d), windows) }))
    .filter(({ w }) => w.typical >= DROP_MIN_TYPICAL_VIEWS && w.change != null && w.change <= DROP_MIN_CHANGE)
    .map(({ d, w }) => {
      const hits30 = metrics.hits(d);
      const ownVpv = hits30 > 0 ? metrics.earnings(d) / hits30 : 0;
      const lostMonth = ((w.typical - w.recent) * 30) / 7;
      const valueUsd = lostMonth * (ownVpv > 0 ? ownVpv : fleetVpv);
      const id = encodeURIComponent(d.id);
      return {
        id: `drop:${d.id}`,
        kind: 'drop',
        tier: TIERS.value,
        score: valueUsd + lostMonth * 1e-6,
        domainId: d.id,
        site: siteName(d, d.id),
        title: `Find out why ${metrics.trafficDaily ? 'human ' : 'page '}views fell ${pct(w.change)}`,
        why: `${fmtInt(w.recent)} ${viewsWord(metrics)} in the last 7 days against a typical ${fmtInt(w.typical)} a week `
          + '(from the median day of the 3 weeks before). Check Rankings for lost positions and the timeline for recent changes.',
        valueUsd,
        estimated: !(ownVpv > 0),
        link: { to: `/domain/${id}/rankings`, label: 'Rankings' },
        links: [{ to: `/domain/${id}`, label: 'Site' }],
      };
    });

/* Biggest weekly gains and losses in page views, by absolute change against
   the typical week: { up: [{ domain, recent, typical, change, delta }], down }.
   A site has to move 10%+ to count: a big site's ordinary wobble is not news. */
export const MOVER_MIN_VIEWS = 50;
export const MOVER_MIN_CHANGE = 0.1;
export const siteMovers = (domains, metrics, windows, { n = 3, hideParked = true } = {}) => {
  const rows = (domains || [])
    .filter((d) => !(hideParked && isParked(d, metrics.earnings(d))))
    .map((d) => ({ domain: d, ...weekChange(viewsDaily(metrics)(d), windows) }))
    .filter((r) => Math.max(r.recent, r.typical) >= MOVER_MIN_VIEWS)
    .filter((r) => r.change == null || Math.abs(r.change) >= MOVER_MIN_CHANGE)
    .map((r) => ({ ...r, delta: r.recent - r.typical }));
  return {
    up: rows.filter((r) => r.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, n),
    down: rows.filter((r) => r.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, n),
  };
};

/* ─────────────────────────────── messages ───────────────────────────── */

const MESSAGE_TYPES = [
  { type: 'page_feedback', filter: 'feedback', one: 'reader feedback note', verb: 'Answer' },
  { type: 'contact_form', filter: 'contact', one: 'contact form message', verb: 'Reply to' },
];

/* Unhandled reader feedback and contact form messages, one item per site and
   type, from /emails/alert-summary's by_type_domain. Someone is waiting on
   these, so they sit with the follow-ups due today. */
export const messageItems = (summary, domainsById = {}) => {
  if (!summary || summary.error) return [];
  const byTypeDomain = summary.by_type_domain || {};
  const out = [];
  MESSAGE_TYPES.forEach((t) => {
    Object.entries(byTypeDomain[t.type] || {}).forEach(([domainId, n]) => {
      if (!(n > 0)) return;
      const domain = domainsById[domainId];
      const params = new URLSearchParams({ filter: t.filter });
      if (domainId) params.set('domain', domainId);
      out.push({
        id: `msg:${t.type}:${domainId}`,
        kind: 'inbox',
        tier: TIERS.due,
        // below a follow-up due today (score 0), more messages first
        score: -1 + n * 1e-3,
        domainId: domainId || null,
        site: domainId ? siteName(domain, domainId) : 'Unknown site',
        title: `${t.verb} ${fmtInt(n)} ${t.one}${n === 1 ? '' : 's'}`,
        why: 'Not marked handled yet. Mark each one handled in the inbox once it is dealt with.',
        valueUsd: null,
        link: { to: `/emails?${params.toString()}`, label: 'Open inbox' },
        links: [],
      });
    });
  });
  return out;
};

/* ─────────────────────────────── ranking ────────────────────────────── */

export const rankItems = (items) => [...(items || [])].sort((a, b) =>
  (a.tier - b.tier) || ((b.score || 0) - (a.score || 0)) || String(a.id).localeCompare(String(b.id)));

export const filterKind = (items, kind) =>
  (!kind || kind === 'all' ? items : (items || []).filter((i) => i.kind === kind));

export const kindCounts = (items) => {
  const out = { all: (items || []).length };
  (items || []).forEach((i) => { out[i.kind] = (out[i.kind] || 0) + 1; });
  return out;
};

/* Domain lookup that also answers for www./apex variants of the id. */
export const indexDomains = (domains) => {
  const out = {};
  (domains || []).forEach((d) => {
    out[d.id] = d;
    const host = normalizeHost(d.hostname || d.name || '');
    if (host) out[host.replace(/\./g, '_')] = out[host.replace(/\./g, '_')] || d;
  });
  return out;
};
